import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { AppShell } from "./components/layout/AppShell";
import LoginPage from "./pages/LoginPage";
import PortalChooser from "./pages/PortalChooser";
import DashboardPage from "./pages/DashboardPage";
import BillingOverviewPage from "./pages/BillingOverviewPage";
import SupportTicketsPage from "./pages/SupportTicketsPage";
import UserManualPage from "./pages/UserManualPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import UsersPage from "./pages/UsersPage";
import VehiclesPage from "./pages/VehiclesPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import CompliancePage from "./pages/CompliancePage";
import AlertsPage from "./pages/AlertsPage";
import AuditPage from "./pages/AuditPage";
import TelematicsPage from "./pages/TelematicsPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import ProfilePage from "./pages/ProfilePage";
import OrganisationProfilePage from "./pages/OrganisationProfilePage";
import TelematicsSettingsPage from "./pages/TelematicsSettingsPage";
import ComplianceHealthPage from "./pages/ComplianceHealthPage";
import ClientHealthPage from "./pages/ClientHealthPage";
import PlatformSettingsPage from "./pages/PlatformSettingsPage";
import VehicleCardPage from "./pages/VehicleCardPage";
import PlatformBrandingPage from "./pages/PlatformBrandingPage";
import BillingSnapshotPage from "./pages/BillingSnapshotPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordRecoveryPages";
import WorkOrdersPage from "./pages/WorkOrdersPage";
import WorkOrderPage from "./pages/WorkOrderPage";
import DriverWorkloadPage from "./pages/DriverWorkloadPage";
import TelematicsDevicesPage from "./pages/TelematicsDevicesPage";
import TelematicsSyncLogsPage from "./pages/TelematicsSyncLogsPage";
import CarPoolPage from "./pages/CarPoolPage";
import CarPoolCalendarPage from "./pages/CarPoolCalendarPage";
import CarPoolUsagePage from "./pages/CarPoolUsagePage";
import DriversPage from "./pages/DriversPage";
import CarPoolSettingsPage from "./pages/CarPoolSettingsPage";
import CrmPage from "./pages/CrmPage";
import InvoicesPage from "./pages/InvoicesPage";
import BillingSettingsPage from "./pages/BillingSettingsPage";
import ServiceDuePage from "./pages/ServiceDuePage";
import ReportsPage from "./pages/ReportsPage";
import ComplianceReportPage from "./pages/ComplianceReportPage";

function LegacyClientRedirect() {
  const { id } = useParams();
  return <Navigate to={`/bo/clients/${id}`} replace />;
}

const soon = (title: string, eyebrow?: string) => <ComingSoonPage title={title} eyebrow={eyebrow} />;

function Guard({ type, children }: { type: "super_admin" | "client_admin"; children: React.ReactElement }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Loading ENFLEET…</div>;
  if (!user) return <Navigate to={type === "super_admin" ? "/bo/login" : "/login"} state={{ from: loc }} replace />;
  if (user.user_type !== type) return <Navigate to={user.user_type === "super_admin" ? "/bo" : "/app"} replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/portals" element={<PortalChooser />} />
          <Route path="/login" element={<LoginPage portal="client" />} />
          <Route path="/bo/login" element={<LoginPage portal="backoffice" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/v/:id" element={<VehicleCardPage />} />
          <Route path="/bo" element={<Guard type="super_admin"><AppShell portal="backoffice" /></Guard>}>
            <Route index element={<DashboardPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientDetailPage />} />
            <Route path="tenants" element={<Navigate to="/bo/clients" replace />} />
            <Route path="tenants/:id" element={<LegacyClientRedirect />} />
            <Route path="users" element={<Navigate to="/bo/settings/users" replace />} />
            <Route path="settings/users" element={<UsersPage />} />
            <Route path="vehicles" element={<VehiclesPage />} />
            <Route path="vehicles/service" element={<ServiceDuePage />} />
            <Route path="vehicles/:id" element={<VehicleDetailPage />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="compliance/health" element={<Navigate to="/bo/dashboard/compliance-health" replace />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="telematics" element={<TelematicsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="dashboard/client-health" element={<ClientHealthPage />} />
            <Route path="dashboard/compliance-health" element={<ComplianceHealthPage />} />
            <Route path="clients/health" element={<Navigate to="/bo/dashboard/client-health" replace />} />
            <Route path="clients/onboarding" element={<CrmPage />} />
            <Route path="clients/support" element={<SupportTicketsPage />} />
            <Route path="manual" element={<UserManualPage />} />
            <Route path="clients/invoices" element={<Navigate to="/bo/billing/invoices" replace />} />
            <Route path="clients/billing" element={<Navigate to="/bo/billing/snapshot" replace />} />
            <Route path="billing" element={<Navigate to="/bo/billing/invoices" replace />} />
            <Route path="billing/invoices" element={<InvoicesPage />} />
            <Route path="billing/snapshot" element={<BillingSnapshotPage />} />
            <Route path="settings/users/roles" element={<Navigate to="/bo/settings/users" replace />} />
            <Route path="alerts/notifications" element={soon("Notification Log", "Alert Center")} />
            <Route path="telematics/devices" element={<TelematicsDevicesPage />} />
            <Route path="telematics/sync-logs" element={<TelematicsSyncLogsPage />} />
            <Route path="requests" element={<WorkOrdersPage />} />
            <Route path="requests/:id" element={<WorkOrderPage />} />
            <Route path="carpool" element={<CarPoolPage />} />
            <Route path="carpool/usage" element={<CarPoolUsagePage />} />
            <Route path="carpool/drivers" element={<DriversPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="reports/compliance" element={<ComplianceReportPage />} />
            <Route path="reports/drivers" element={<DriverWorkloadPage />} />
            <Route path="reports/billing" element={<Navigate to="/bo/billing/snapshot" replace />} />
            <Route path="settings" element={<PlatformSettingsPage />} />
            <Route path="settings/email" element={<Navigate to="/bo/settings" replace />} />
            <Route path="settings/modules" element={<Navigate to="/bo/settings" replace />} />
            <Route path="settings/branding" element={<PlatformBrandingPage />} />
            <Route path="settings/billing" element={<BillingSettingsPage />} />
          </Route>
          <Route path="/app" element={<Guard type="client_admin"><AppShell portal="client" /></Guard>}>
            <Route index element={<DashboardPage />} />
            <Route path="vehicles" element={<VehiclesPage />} />
            <Route path="vehicles/service" element={<ServiceDuePage />} />
            <Route path="vehicles/:id" element={<VehicleDetailPage />} />
            <Route path="departments" element={<Navigate to="/app/settings/organizations" replace />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="compliance/health" element={<Navigate to="/app/dashboard/compliance-health" replace />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="telematics" element={<TelematicsPage />} />
            <Route path="users" element={<Navigate to="/app/settings/users" replace />} />
            <Route path="settings/users" element={<UsersPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="dashboard/compliance-health" element={<ComplianceHealthPage />} />
            <Route path="alerts/notifications" element={soon("Notification Log", "Alert Center")} />
            <Route path="telematics/devices" element={<TelematicsDevicesPage />} />
            <Route path="telematics/sync-logs" element={<TelematicsSyncLogsPage />} />
            <Route path="requests" element={<WorkOrdersPage />} />
            <Route path="requests/:id" element={<WorkOrderPage />} />
            <Route path="settings/approvals" element={<Navigate to="/app/settings" replace />} />
            <Route path="carpool" element={<CarPoolPage />} />
            <Route path="carpool/calendar" element={<CarPoolCalendarPage />} />
            <Route path="carpool/usage" element={<CarPoolUsagePage />} />
            <Route path="carpool/drivers" element={<Navigate to="/app/settings/carpool" replace />} />
            <Route path="carpool/settings" element={<Navigate to="/app/settings/carpool" replace />} />
            <Route path="settings/carpool" element={<CarPoolSettingsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="reports/compliance" element={<ComplianceReportPage />} />
            <Route path="reports/drivers" element={<DriverWorkloadPage />} />
            <Route path="settings" element={<OrganisationProfilePage />} />
            <Route path="settings/organizations" element={<DepartmentsPage />} />
            <Route path="settings/telematics" element={<TelematicsSettingsPage />} />
            <Route path="settings/access" element={<Navigate to="/app/settings/users" replace />} />
            <Route path="settings/roles" element={<Navigate to="/app/settings/users" replace />} />
            <Route path="settings/branding" element={<Navigate to="/app/settings" replace />} />
            <Route path="billing" element={<BillingOverviewPage />} />
            <Route path="billing/invoices" element={<InvoicesPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
